/**
 * Pure CSV utilities for client-side data export.
 * No external dependencies.
 */

/**
 * Escape a single CSV cell value:
 * - Convert null/undefined to empty string
 * - Wrap in quotes if it contains a comma, quote, or newline
 * - Double any embedded quotes
 */
function escapeCell(value) {
  if (value == null) return ''
  const str = String(value)
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Build a CSV string from headers and rows.
 * @param {string[]} headers - Column headers
 * @param {Array<Array<any>>} rows - 2D array of cell values
 * @returns {string} CSV content
 */
export function buildCsv(headers, rows) {
  const lines = [headers.map(escapeCell).join(',')]
  for (const row of rows) {
    lines.push(row.map(escapeCell).join(','))
  }
  return lines.join('\n')
}

/**
 * Trigger a browser download of a CSV file.
 */
export function downloadCsv(filename, content) {
  // Add BOM so Excel correctly recognizes UTF-8 (important for Arabic notes)
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Convert weight entries array to a CSV string.
 * Entries are expected to be sorted newest-first; we reverse to chronological for export.
 */
export function weightEntriesToCsv(entries) {
  const headers = ['date', 'weight_kg', 'note']
  const sorted = [...entries].sort((a, b) => (a.date < b.date ? -1 : 1))
  const rows = sorted.map((e) => [e.date, e.weight, e.note || ''])
  return buildCsv(headers, rows)
}

/**
 * Convert body measurements array to a CSV string.
 */
export function measurementsToCsv(measurements) {
  const headers = ['date', 'neck_cm', 'chest_cm', 'belly_cm', 'waist_cm', 'arm_cm', 'thigh_cm', 'body_fat_pct', 'note']
  const sorted = [...measurements].sort((a, b) => (a.date < b.date ? -1 : 1))
  const rows = sorted.map((m) => [
    m.date,
    m.neck ?? '',
    m.chest ?? '',
    m.belly ?? '',
    m.waist ?? '',
    m.arm ?? '',
    m.thigh ?? '',
    m.body_fat ?? '',
    m.note || '',
  ])
  return buildCsv(headers, rows)
}
